import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    HostListener,
    input,
    Input,
    OnDestroy,
    OnInit,
    output
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
    ActionItem,
    ActionItemIconPosition,
    ActionItemStyle,
    IconSize,
    ShActionItemComponent
} from '../../../../elements/public_api';
import { scrollToTop } from '../../../../helpers/scroll-to-top.helper';
import { RESULTS_PER_PAGE_OPTIONS } from '../display-settings-bar/entities/display-mode.entity';
import { PaginationInfo, RequestPaginationInfo, ResponsePaginationInfo } from './entities/pagination-info.entity';

@Component({
    standalone: true,
    selector: 'sh-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, ShActionItemComponent]
})
export class ShPaginationComponent implements OnInit, OnDestroy {
    // Cap number of total results because of heavy processing required
    private readonly MAX_RESULTS_LIMIT: number = 15000;
    private readonly destroy$: Subject<void> = new Subject<void>();

    resultsPerPageControl: UntypedFormControl = new UntypedFormControl();

    /** Inputs */
    paginationInfo = input.required<ResponsePaginationInfo | undefined>();

    @Input() previousPageMsg = '';
    @Input() nextPageMsg = '';
    @Input() perPageMsg = '';
    @Input() resultsPerPageOptions: number[] = RESULTS_PER_PAGE_OPTIONS;
    @Input() scrollRef?: string; // Ability to overwrite default scrollTo

    /** Computed */
    $isFirstPage = computed(() => !this.hasPreviousPage(this.paginationInfo()));
    $isLastPage = computed(() => !this.hasNextPage(this.paginationInfo()));
    $previousPageActionItem = computed(() => this.getPreviousActionItem(this.$isFirstPage()));
    $nextPageActionItem = computed(() => this.getNextActionItem(this.$isLastPage()));

    $currentPage = computed(() => this.paginationInfo()?.page ?? 0);
    $maxPage = computed(() => this.calculateMaxPage(this.paginationInfo()));

    $otherPageIndicators = computed(() => this.calculatePageIndicators(this.$currentPage(), this.$maxPage()));

    /** Outputs */
    paginationInfoChanged = output<RequestPaginationInfo>();

    /** Listeners */
    @HostListener('document:keydown.ArrowLeft', ['$event'])
    handleArrowLeft(event: KeyboardEvent) {
        // MED-6189: Only do if not targeting anything else
        if ((event.target as HTMLElement).nodeName === 'BODY') {
            this.onPreviousPage();
        }
    }

    @HostListener('document:keydown.ArrowRight', ['$event'])
    handleArrowRight(event: KeyboardEvent) {
        // MED-6189: Only do if not targeting anything else
        if ((event.target as HTMLElement).nodeName === 'BODY') {
            this.onNextPage();
        }
    }

    constructor() {
        effect(() => {
            this.selectMatchinPageSizeOption(this.paginationInfo());
            this.preventOutOfBoundsNavigation(this.paginationInfo(), this.$maxPage());
        });
    }

    ngOnInit(): void {
        this.initResultsPerPageListeners();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initResultsPerPageListeners(): void {
        this.resultsPerPageControl.valueChanges
            .pipe(
                map(value => value),
                takeUntil(this.destroy$)
            )
            .subscribe(size => {
                const sizeNumber: number = Number.parseInt(size, 10);
                const newPaginationInfo: PaginationInfo = {
                    ...this.paginationInfo(),
                    resultsPerPage: sizeNumber,
                    page: 0
                };
                this.emit(newPaginationInfo);
            });
    }

    private getPreviousActionItem(isFirst: boolean): ActionItem {
        return {
            label: this.previousPageMsg,
            eventListener: event => this.onPreviousPage(),
            icon: 'arrow-left',
            iconPosition: ActionItemIconPosition.Left,
            iconSize: IconSize.SMALL,
            style: ActionItemStyle.Ghost,
            disabled: isFirst
        };
    }

    private getNextActionItem(isLast: boolean): ActionItem {
        return {
            label: this.nextPageMsg,
            eventListener: event => this.onNextPage(),
            icon: 'arrow-right',
            iconPosition: ActionItemIconPosition.Right,
            iconSize: IconSize.SMALL,
            style: ActionItemStyle.Ghost,
            disabled: isLast
        };
    }

    // #region Effects
    private selectMatchinPageSizeOption(pagination: PaginationInfo | undefined): void {
        const resultsPerPage = pagination?.resultsPerPage || this.resultsPerPageOptions[0];

        if (!this.resultsPerPageOptions.includes(resultsPerPage)) {
            this.resultsPerPageOptions.push(resultsPerPage);
        }
        this.resultsPerPageOptions = this.resultsPerPageOptions.sort((a, b) => a - b);
        this.resultsPerPageControl.setValue(resultsPerPage, { emitEvent: false });
    }

    private preventOutOfBoundsNavigation(pagination: PaginationInfo | undefined, maxPage: number): void {
        if (!pagination) {
            return;
        }

        if (pagination.page < 0 || pagination.page > maxPage) {
            this.navigateToPage(0);
        }
    }
    // #endregion

    // #region Actions
    private onPreviousPage(): void {
        const isFirstPage = this.$isFirstPage();

        if (isFirstPage) {
            return;
        }

        this.navigateToPage(this.$currentPage() - 1);
    }

    private onNextPage(): void {
        const isLastPage = this.$isLastPage();

        if (isLastPage) {
            return;
        }

        this.navigateToPage(this.$currentPage() + 1);
    }

    public navigateToPage(page: number): void {
        const pagination = this.paginationInfo();
        const maxPage = this.$maxPage();

        // Prevent navigation to negative page
        page = page < 0 ? 0 : page;
        // Prevent navigation beyond max page
        page = page > maxPage ? maxPage : page;

        if (!pagination) {
            return;
        }

        this.emit({ ...pagination, page });
    }

    private emit(paginationInfo: PaginationInfo): void {
        // MED-1959: When clicking on previous and next, scrollbar did not go up
        // (partly cause it got held back by .sh-list-layout__list (overflow-y: auto))

        if (this.scrollRef) {
            setTimeout(() => scrollToTop(document.querySelector(this.scrollRef!)), 100);
        } else {
            setTimeout(() => scrollToTop(document.querySelector('.sh-list-layout__list')), 100);
            setTimeout(() => scrollToTop(document.querySelector('.sh-scroll-container')), 100);
        }
        this.paginationInfoChanged.emit(paginationInfo);
    }
    // #endregion

    // #region Utils
    private hasNextPage(pi: ResponsePaginationInfo | undefined): boolean {
        return !!pi && pi.page < this.$maxPage();
    }

    private hasPreviousPage(pi: PaginationInfo | undefined): boolean {
        return !!pi && pi.page !== 0;
    }

    private calculateMaxPage(pi: ResponsePaginationInfo | undefined): number {
        const resultsPerPage = pi?.resultsPerPage ?? 10;

        const totalNumberOfResults = pi?.totalNumberOfResults ?? 0;

        const results = totalNumberOfResults > this.MAX_RESULTS_LIMIT ? this.MAX_RESULTS_LIMIT : totalNumberOfResults;

        const totalPages = Math.ceil(results / resultsPerPage);

        return totalPages > 0 ? totalPages - 1 : 0;
    }

    private calculatePageIndicators(currentPage: number, maxPage: number): number[] {
        const pages = [];

        // If on page 1 to 4, add page indicators 2 to 5 as long as there are as many result pages
        if (currentPage < 4) {
            for (let pageIndex = 1; pageIndex <= 4; pageIndex++) {
                // Never add last page
                if (pageIndex < maxPage) {
                    pages.push(pageIndex);
                } else {
                    break;
                }
            }

            return pages;
        }

        // If on last 4 pages, add page indicators of 2nd, 3rd and 4th to last pages
        if (currentPage > maxPage - 4) {
            for (let pageIndex = maxPage - 4; pageIndex < maxPage; pageIndex++) {
                // Never add first page
                if (pageIndex > 0) {
                    pages.push(pageIndex);
                }
            }

            return pages;
        }

        // If anywhere else, add page indicators for current and adjacent pages
        return [currentPage - 1, currentPage, currentPage + 1];
    }
    // #endregion
}
